import { formatoPrecio } from '@/lib/utils/formato-precio';
import type {
  EstadoDeLecturaPrixia,
  LecturaPrixiaDeLaEmpresa,
  ProgramaDeLecturaPrixia,
  TipoDeLecturaPrixia,
} from '../types/dynamic-pricing.types';

/** Textos de las lecturas de Prixia para la empresa (plan 298), sin nombres internos. */
export const NOMBRE_DE_LECTURA: Record<TipoDeLecturaPrixia, string> = {
  PRICE_CHECK: 'Chequeo de precios',
  FULL_RESCRAPE: 'Barrido del catálogo',
};

/** C10: nunca el nombre de otra empresa. */
export const ORIGEN_DE_LECTURA: Record<LecturaPrixiaDeLaEmpresa['source'], string> = {
  SCHEDULE: 'Programada',
  YOUR_COMPANY: 'Pedida por tu empresa',
  PRIXIA: 'Lectura de Prixia',
};

export const ESTADO_DE_LECTURA_PRIXIA: Record<LecturaPrixiaDeLaEmpresa['status'], string> = {
  QUEUED: 'En cola',
  RUNNING: 'Leyendo',
  COMPLETED: 'Terminada',
  FAILED: 'Falló',
  CANCELLED: 'Cancelada',
};

const UNIDADES: Record<ProgramaDeLecturaPrixia['intervalUnit'], [string, string]> = {
  HOURS: ['hora', 'horas'],
  DAYS: ['día', 'días'],
  WEEKS: ['semana', 'semanas'],
};

/**
 * «cada día», «cada 2 semanas» (L4, PD-50: las tareas propias decían «cada 1 día(s)»).
 * La misma forma que el calendario de Prixia, así las dos agendas se leen igual.
 */
export function cadaCuanto(valor: number, unidad: ProgramaDeLecturaPrixia['intervalUnit']): string {
  const [uno, varios] = UNIDADES[unidad];
  return valor === 1 ? `cada ${uno}` : `cada ${valor} ${varios}`;
}

/** «cada día», «cada 12 horas, a las 03:00», «apagado», «sin programar». */
export function programaEnTexto(p: ProgramaDeLecturaPrixia | null | undefined): string {
  if (!p) return 'sin programar';
  if (!p.isActive) return 'apagado';
  const [uno, varios] = UNIDADES[p.intervalUnit];
  const cada = p.intervalValue === 1 ? `cada ${uno}` : `cada ${p.intervalValue} ${varios}`;
  const hora =
    p.preferredHour != null
      ? `, a las ${String(p.preferredHour).padStart(2, '0')}:${String(p.preferredMin).padStart(2, '0')}`
      : '';
  return cada + hora;
}

export function fechaCorta(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-BO', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Sin moneda fija: Prixia es para cualquier país (L7). El número sale del formateador único. */
export function precioEnTexto(n: number | null | undefined): string {
  if (n == null) return '—';
  return formatoPrecio(n);
}

export function duracionDeLectura(desde: string | null, hasta: string | null): string {
  if (!desde || !hasta) return '—';
  const ms = new Date(hasta).getTime() - new Date(desde).getTime();
  if (ms < 60_000) return `${Math.max(1, Math.round(ms / 1000))} s`;
  const minutos = Math.round(ms / 60_000);
  if (minutos < 60) return `${minutos} min`;
  return `${Math.floor(minutos / 60)} h ${minutos % 60} min`;
}

/**
 * El cupo de «leer ahora» del día dicho para la empresa (revisión T6+T7, 14): con cupo 0
 * no está habilitado, y agotado o no, sumarse a una lectura en curso no lo gasta.
 */
export function cupoEnTexto(readNow: { dailyQuota: number; remainingToday: number }): string {
  if (readNow.dailyQuota === 0) {
    return '"Leer ahora" no está habilitado para tu empresa; si ya hay una lectura en curso, tu pedido se suma.';
  }
  const verbo = readNow.remainingToday === 1 ? 'Queda' : 'Quedan';
  const lecturas = readNow.dailyQuota === 1 ? 'lectura nueva' : 'lecturas nuevas';
  const base = `${verbo} ${readNow.remainingToday} de ${readNow.dailyQuota} ${lecturas} hoy`;
  return readNow.remainingToday === 0
    ? `${base}; si ya hay una en curso, tu pedido se suma sin gastar cupo`
    : base;
}

/** La misma frase que el 403 del servidor (D-291-11). */
export const TEXTO_SIN_PRIMER_BARRIDO =
  'Esta tienda todavía no tiene su primer barrido: lo pide un administrador.';

/**
 * Revisión de la rama S3: un vendedor sólo puede «Chequear ahora» una tienda sin su
 * primer barrido si ya hay un barrido en curso o en cola (se suma sin gastar cupo); si
 * no, el servidor lo rechaza y el botón no se ofrece.
 */
export function vendedorSinPrimerBarrido(
  estado: EstadoDeLecturaPrixia | null | undefined,
  esAdmin: boolean
): boolean {
  if (esAdmin || !estado) return false;
  return !estado.lastSweepAt && estado.inProgress?.type !== 'FULL_RESCRAPE';
}

/** «Te queda 1 lectura nueva hoy.» / «Te quedan 3 lecturas nuevas hoy.» */
export function restantesEnTexto(n: number): string {
  return n === 1 ? 'Te queda 1 lectura nueva hoy.' : `Te quedan ${n} lecturas nuevas hoy.`;
}

/**
 * Una cantidad de horas dicha como la diría una persona: «6 horas», «1 día», «7 días»,
 * «8 semanas». Semanas sólo desde 2 enteras (168 h se dice «7 días»).
 */
export function horasEnTexto(horas: number): string {
  const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;
  if (horas % 168 === 0 && horas >= 336) return plural(horas / 168, 'semana', 'semanas');
  if (horas % 24 === 0) return plural(horas / 24, 'día', 'días');
  return plural(horas, 'hora', 'horas');
}
